"use client";

import { Button } from "@pex/ui-web";
import { motion, useMotionValue, useSpring } from "framer-motion";
import Image from "next/image";
import { useRef, useState } from "react";
import { IconPlayRing } from "@/components/icons/IconPlayRing";
import { IconPlayTriangle } from "@/components/icons/IconPlayTriangle";
import { ScrollLink } from "@/components/scroll-link";

const YOUTUBE_ID = "BNWWL11Szko";

// How far (px) the button can travel from its resting centre
const MAX_TRAVEL = 56;

export function ErsHero() {
  const containerRef = useRef<HTMLDivElement>(null);
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const x = useSpring(rawX, { stiffness: 60, damping: 18, mass: 1.4 });
  const y = useSpring(rawY, { stiffness: 60, damping: 18, mass: 1.4 });

  const [playing, setPlaying] = useState(false);

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const halfW = rect.width / 2;
    const halfH = rect.height / 2;
    rawX.set((dx / halfW) * MAX_TRAVEL);
    rawY.set((dy / halfH) * MAX_TRAVEL);
  }

  function handleMouseLeave() {
    rawX.set(0);
    rawY.set(0);
  }

  return (
    <section className="overflow-hidden bg-white pt-16 sm:pt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl space-y-6 text-center">
          <h1 className="heading-1 font-bold">Exodus Recovery Skills</h1>
          <p className="body-lg mx-auto max-w-2xl text-muted-foreground">
            A foundational programme for addiction recovery based on decades of
            rehabilitation experience and expertise. ERS delivers vital
            knowledge, insights, and skills for lasting freedom through a Learn,
            Reflect, Apply methodology.
          </p>
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild className="w-full sm:w-auto" variant="fancy">
              <ScrollLink href="#how-to-access">Access ERS</ScrollLink>
            </Button>
            <Button
              asChild
              className="w-full sm:w-auto"
              variant="secondary-fancy"
            >
              <ScrollLink href="#methodology">Learn More</ScrollLink>
            </Button>
          </div>
        </div>
      </div>

      {/* Hero — video thumbnail with magnetic play button; plays YouTube on click */}
      <div className="mx-auto mt-12 max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: mouse tracking for magnetic play button visual effect */}
        {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: mouse tracking for magnetic play button visual effect */}
        <div
          className="relative aspect-video overflow-hidden rounded-2xl bg-zinc-900"
          onMouseLeave={handleMouseLeave}
          onMouseMove={playing ? undefined : handleMouseMove}
          ref={containerRef}
        >
          {/* Thumbnail + magnetic play button — fades out when video starts */}
          <div
            className="absolute inset-0 transition-opacity duration-700"
            style={{
              opacity: playing ? 0 : 1,
              pointerEvents: playing ? "none" : "auto",
            }}
          >
            <Image
              alt='"Exodus Recovery Skills" video thumbnail with a smiling woman reading.'
              className="object-cover"
              fill
              priority
              sizes="(max-width: 1280px) 100vw, 1280px"
              src={"/images/programmes/ers/ERS_VideoThumbnail.webp"}
            />
            {/* Magnetic play button — centered in image */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <motion.div
                className="pointer-events-auto relative h-24 w-24 cursor-pointer"
                onClick={() => setPlaying(true)}
                style={{ x, y }}
              >
                {/* Subtle backdrop circle for readability */}
                <div
                  aria-hidden="true"
                  className="absolute inset-[-16px] rounded-full backdrop-blur-[3px]"
                  style={{ background: "rgba(0,0,0,0.22)" }}
                />
                {/* Rotating "WATCH THE OVERVIEW" circular text ring */}
                <motion.div
                  animate={{ rotate: -360 }}
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{ willChange: "transform" }}
                  transition={{
                    duration: 12,
                    ease: "linear",
                    repeat: Number.POSITIVE_INFINITY,
                  }}
                >
                  <IconPlayRing className="h-full w-full" />
                </motion.div>
                {/* Static play icon at centre */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <IconPlayTriangle height={50} width={50} />
                </div>
              </motion.div>
            </div>
          </div>

          {/* YouTube iframe — fades in when play is clicked */}
          {playing && (
            <iframe
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 h-full w-full"
              src={`https://www.youtube.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0`}
              title="Exodus Recovery Skills Overview"
            />
          )}
        </div>
      </div>
    </section>
  );
}
